/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
/* eslint-disable spire/fenced-imports */
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddToCart } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import MessagePanel from "@monsma/App/Components/MessagePanel/MessagePanel";
import { hasEnoughInventory } from "@monsma/App/Handlers/Data/Products/ProductsSelectors";
import * as React from "react";
import { connect } from "react-redux";

type Props = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>; // xng-change

// xng-change: Adding more props we need
const mapStateToProps = (state: ApplicationState, props: HasProductContext) => {
    const configurationCompleted = state.pages.productDetails.configurationCompleted;
    const variantSelectionCompleted = state.pages.productDetails.variantSelectionCompleted;

    return {
        configurationCompleted,
        variantSelectionCompleted,
        productSettings: getSettingsCollection(state).productSettings,
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            configurationCompleted,
            variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

export interface ProductDetailsAvailabilityStyles {
    availability?: ProductAvailabilityStyles;
}

export const availabilityStyles: ProductDetailsAvailabilityStyles = {};

const styles = availabilityStyles;

// xng-change: Passing the new props we've added.
const ProductDetailsAvailability = ({
    productContext: { product },
    configurationCompleted,
    productSettings,
    canAddToCart,
    hasEnoughInventory,
}: Props) => {
    if (!product) {
        return null;
    }

    const ableToAddToCart = productSettings.canAddToCart && hasEnoughInventory && canAddToCart; // xng-change

    // xng-change: Update the template by adding Message panel component
    return (
        <>
            <ProductContextAvailability
                isProductDetailsPage={true}
                configurationCompleted={configurationCompleted}
                extendedStyles={styles.availability}
            />
            {!product.isVariantParent && !ableToAddToCart && (
                <MessagePanel messageName="Custom_PDP_OutOfStock_Info" extendedStyles={(styles as any).messagePanel} />
            )}
            {!product.isVariantParent && ableToAddToCart && (
                <MessagePanel messageName="Custom_PDP_InStock_Info" extendedStyles={(styles as any).messagePanel} />
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps)(ProductDetailsAvailability)), // xng-change
    definition: {
        displayName: "Availability",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
